import { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

import ContactModal from "../../../../Assets/Common/ContactModal";

const CTASection = ({ data }) => {
  const [openModal, setOpenModal] = useState(false);

  if (!data) return null;

  /* ============================================================
     CONTACT MODAL DATA
  ============================================================ */

  const contactService = "Legal Services";

  /* ============================================================
     LEGAL SUB-SERVICE MAP

     URL:
     /services/legal-services/business-registration

     becomes:
     business-registration

     and is converted to:
     Business Registration
  ============================================================ */

  const legalSubServiceMap = {
    "business-registration": "Business Registration",
    "company-registration": "Company Registration",
    "taxation-services": "Taxation Services",
    "iso-certification": "ISO Certification",
    "ipr-services": "IPR Services",
  };

  /* ============================================================
     GET CURRENT SUB-SERVICE FROM URL
  ============================================================ */

  const getSubServiceFromUrl = () => {
    try {
      const pathname = window.location.pathname;

      const segments = pathname
        .split("/")
        .filter(Boolean);

      /*
        Example:

        /services/legal-services/business-registration

        segments:
        [
          "services",
          "legal-services",
          "business-registration"
        ]

        Last segment:
        "business-registration"
      */

      const subServiceSlug =
        segments[segments.length - 1];

      return (
        legalSubServiceMap[subServiceSlug] || ""
      );
    } catch (error) {
      console.error(
        "Unable to detect legal sub-service:",
        error
      );

      return "";
    }
  };

  const contactSubService =
    getSubServiceFromUrl();

  /* ============================================================
     DEBUG

     You can remove this later.
  ============================================================ */

  console.log(
    "CTA Contact Service:",
    contactService
  );

  console.log(
    "CTA Contact Sub-Service:",
    contactSubService
  );

  /* ============================================================
     STATS
  ============================================================ */

  const stats = [
    {
      value: "100%",
      label: "Transparent Process",
    },
    {
      value: "Fast",
      label: "Government Filing",
    },
    {
      value: "Expert",
      label: "Legal Support",
    },
    {
      value: "24×7",
      label: "Customer Assistance",
    },
  ];

  return (
    <>
      {/* ==========================================================
          CTA SECTION
      ========================================================== */}

      <section
        className="
          relative
          overflow-hidden
          py-8

          sm:py-12

          lg:py-24

          theme-bg-primary
        "
      >
        {/* ========================================================
            BACKGROUND GLOW
        ======================================================== */}

        <div
          className="
            pointer-events-none
            absolute
            left-1/2
            top-1/2
            h-[220px]
            w-[220px]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            bg-orange-500/10
            blur-[120px]

            sm:h-[280px]
            sm:w-[280px]

            lg:h-[320px]
            lg:w-[320px]
            lg:blur-[170px]
          "
        />

        {/* ========================================================
            MAIN CONTAINER
        ======================================================== */}

        <div
          className="
            relative
            mx-auto
            max-w-7xl
            px-5

            sm:px-6

            lg:px-8
          "
        >
          <motion.div
            initial={{
              opacity: 0,
              y: 50,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.7,
            }}
            className="
              overflow-hidden
              rounded-[24px]

              border
              border-orange-500/15

              theme-card

              p-6

              shadow-[0_20px_60px_var(--shadow-color)]

              sm:rounded-[28px]
              sm:p-8

              lg:rounded-[32px]
              lg:p-12
            "
          >
            <div
              className="
                grid
                items-center
                gap-10

                lg:grid-cols-[1.4fr_.8fr]
                lg:gap-12
              "
            >
              {/* ==================================================
                  LEFT CONTENT
              ================================================== */}

              <div>
                {/* ==================================================
                    BADGE
                ================================================== */}

                <span
                  className="
                    inline-flex
                    rounded-full

                    border
                    border-orange-500/20
                    bg-orange-500/10

                    px-4
                    py-2

                    text-[11px]
                    font-semibold
                    uppercase
                    tracking-[2px]

                    text-orange-400

                    sm:text-xs
                    sm:tracking-[3px]
                  "
                >
                  Ready to Get Started?
                </span>

                {/* ==================================================
                    HEADING
                ================================================== */}

                <h2
                  className="
                    mt-5
                    text-3xl
                    font-bold
                    leading-tight

                    theme-text-primary

                    sm:text-4xl

                    lg:mt-6
                    lg:text-5xl
                  "
                >
                  {data.title}
                </h2>

                {/* ==================================================
                    DESCRIPTION
                ================================================== */}

                <p
                  className="
                    mt-5
                    max-w-2xl
                    text-base
                    leading-7

                    theme-text-secondary

                    sm:text-lg
                    sm:leading-8

                    lg:mt-6
                  "
                >
                  {data.description}
                </p>

                {/* ==================================================
                    TRUST POINTS
                ================================================== */}

                <div
                  className="
                    mt-7
                    flex
                    flex-wrap
                    gap-x-6
                    gap-y-3

                    lg:mt-8
                  "
                >
                  <div
                    className="
                      flex
                      items-center
                      gap-2

                      text-sm

                      theme-text-secondary
                    "
                  >
                    <CheckCircle2
                      size={18}
                      className="
                        shrink-0
                        brand-color
                      "
                    />

                    Fast Registration
                  </div>

                  <div
                    className="
                      flex
                      items-center
                      gap-2

                      text-sm

                      theme-text-secondary
                    "
                  >
                    <CheckCircle2
                      size={18}
                      className="
                        shrink-0
                        brand-color
                      "
                    />

                    Expert Assistance
                  </div>

                  <div
                    className="
                      flex
                      items-center
                      gap-2

                      text-sm

                      theme-text-secondary
                    "
                  >
                    <CheckCircle2
                      size={18}
                      className="
                        shrink-0
                        brand-color
                      "
                    />

                    100% Compliance
                  </div>
                </div>

                {/* ==================================================
                    CTA BUTTON
                ================================================== */}

                <div
                  className="
                    mt-8

                    lg:mt-10
                  "
                >
                  <motion.button
                    type="button"
                    whileHover={{
                      scale: 1.03,
                    }}
                    whileTap={{
                      scale: 0.97,
                    }}
                    onClick={() => setOpenModal(true)}
                    className="
                      group
                      inline-flex
                      items-center
                      gap-3
                      rounded-xl

                      brand-bg

                      px-6
                      py-3.5

                      font-semibold
                      text-white

                      transition-all
                      duration-300

                      hover:bg-orange-600
                      hover:shadow-[0_10px_30px_rgba(255,136,0,.25)]

                      sm:px-7
                      sm:py-4
                    "
                  >
                    <span>
                      {data.buttonText ||
                        "Book Free Consultation"}
                    </span>

                    <ArrowRight
                      size={18}
                      className="
                        transition-transform
                        duration-300
                        group-hover:translate-x-1
                      "
                    />
                  </motion.button>
                </div>
              </div>

              {/* ==================================================
                  RIGHT STATS
              ================================================== */}

              <div
                className="
                  grid
                  grid-cols-2
                  gap-3

                  sm:gap-4
                "
              >
                {stats.map((item, index) => (
                  <motion.div
                    key={index}
                    whileHover={{
                      y: -5,
                      scale: 1.02,
                    }}
                    transition={{
                      duration: 0.25,
                    }}
                    className="
                      rounded-2xl

                      border
                      border-orange-500/10

                      theme-card-secondary

                      p-4
                      text-center

                      shadow-[0_10px_30px_var(--shadow-color)]

                      transition-all
                      duration-300

                      hover:border-orange-500/30

                      hover:bg-orange-500/[0.04]

                      sm:p-5
                    "
                  >
                    <h3
                      className="
                        text-2xl
                        font-bold

                        brand-color

                        sm:text-3xl
                      "
                    >
                      {item.value}
                    </h3>

                    <p
                      className="
                        mt-2
                        text-xs
                        leading-5

                        theme-text-muted

                        sm:text-sm
                        sm:leading-6
                      "
                    >
                      {item.label}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================================================
          CONTACT MODAL
      ============================================================ */}

      <ContactModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        initialService={contactService}
        initialSubService={contactSubService}
        lockService={true}
        lockSubService={true}
      />
    </>
  );
};

export default CTASection;  